// src/modules/orion-shell/OrionShell.tsx
//
// Shell «Orion» — barra lateral índigo + barra superior. Reemplaza a <Layout>
// SOLO para orgs con `custom_overrides.dashboard.shell = "orion"` (ver
// DashboardShellContext). Las rutas, ModuleRoute, pages.tsx y todo el gating
// son los mismos: aquí solo cambia el marco.
//
// Accesibilidad: enlace «Saltar al contenido», landmarks (nav/header/main),
// aria-current en la entrada activa, cajón móvil con Esc + foco devuelto,
// buscador ⌘K/Ctrl+K navegable con flechas, foco visible (index.css).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Bell,
  Building2,
  Check,
  ChevronDown,
  Cpu,
  ExternalLink,
  Briefcase,
  LayoutGrid,
  MessagesSquare,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  Menu,
  Moon,
  Plus,
  Search,
  Sun,
  UserRound,
  X,
} from 'lucide-react';

import { useModules } from '@/context/ModulesContext';
import { useDashboardShell } from '@/context/DashboardShellContext';
import { decodeJwt } from '@/lib/api_superadmin';
import { resolveItemLabel, resolveModule } from '@/modules';
import { SHELL_NAV, canOpen, canReviewSite, ownsRoute, resolveTarget, toPath, type Access } from './nav';
import { themeVars } from './theme';
import { useCompanyName, useMyName, usePostStatusCounts, useRevisions, useSupervisorAlerts } from './data';
import { attendSupervisorAlert, type SupervisorAlert } from '@/lib/api';
import { decideTicket, useCtoPending, type CtoPendingItem } from './tickets/data';
import { CommandPalette } from './CommandPalette';
import { shellBrand } from './brand';
import { SidebarChats } from './SidebarChats';
import { AgentDock, NAV_WORDS, type DockTarget } from './AgentDock';

const THEME_KEY = 'orion_theme'; // mismo que <Layout>: la preferencia se comparte

function useThemeMode(): ['light' | 'dark', () => void] {
  const [mode, setMode] = useState<'light' | 'dark'>(() =>
    (localStorage.getItem(THEME_KEY) as 'light' | 'dark') === 'dark' ? 'dark' : 'light',
  );
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    try {
      localStorage.setItem(THEME_KEY, mode);
    } catch {
      /* private mode */
    }
  }, [mode]);
  return [mode, () => setMode((m) => (m === 'dark' ? 'light' : 'dark'))];
}

function tokenClaims(): { userId: string; email: string; orgId: string; isCto: boolean } {
  const t = localStorage.getItem('auth_token');
  const p: any = t ? decodeJwt(t) : null;
  const roles: unknown[] = Array.isArray(p?.role) ? p.role : [p?.role, ...(Array.isArray(p?.roles) ? p.roles : [])];
  return {
    userId: typeof p?.user_id === 'string' ? p.user_id : '',
    email: typeof p?.sub === 'string' ? p.sub : '',
    orgId: typeof p?.org_id === 'string' ? p.org_id : '',
    isCto: roles.some((r) => String(r ?? '').trim().toLowerCase() === 'cto'),
  };
}

/** Menú desplegable mínimo y accesible (botón + lista; Esc / clic fuera cierra). */
function useDisclosure() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
}

interface OrionShellProps {
  children: React.ReactNode;
  onLogout: () => void;
}

export function OrionShell({ children, onLogout }: OrionShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ moduleKey?: string; tab?: string }>();
  const { availableModules, isAdmin, isDev, rememberModule, archetype, sovereign } = useModules();
  const { theme, config } = useDashboardShell();
  const brand = useMemo(() => shellBrand(config), [config]);
  const [mode, toggleMode] = useThemeMode();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Barra lateral: ocultable (escritorio) y con dos vistas, Trabajo (módulos) y
  // Chats (historial). Se recuerdan por navegador. ⌘/Ctrl+B la oculta o muestra.
  const [collapsed, setCollapsed] = useState<boolean>(() => { try { return localStorage.getItem('orion:sidebar:collapsed') === '1'; } catch { return false; } });
  const [sideMode, setSideMode] = useState<'trabajo' | 'chats'>(() => { try { return localStorage.getItem('orion:sidebar:mode') === 'chats' ? 'chats' : 'trabajo'; } catch { return 'trabajo'; } });
  useEffect(() => { try { localStorage.setItem('orion:sidebar:collapsed', collapsed ? '1' : '0'); } catch { /* sin almacenamiento */ } }, [collapsed]);
  useEffect(() => { try { localStorage.setItem('orion:sidebar:mode', sideMode); } catch { /* sin almacenamiento */ } }, [sideMode]);
  const toggleSidebar = useCallback(() => {
    if (window.matchMedia('(min-width: 1024px)').matches) setCollapsed((c) => !c);
    else setDrawerOpen((o) => !o);
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'b') { e.preventDefault(); toggleSidebar(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleSidebar]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);

  const claims = useMemo(tokenClaims, []);
  const company = useCompanyName();
  const myName = useMyName(claims.userId);
  const orgLabel = company.data || claims.orgId || 'Tu organización';
  const displayName = myName.data || claims.email;

  const access: Access = { availableModules, isAdmin, isDev, sovereign };
  // Destinos del agente flotante: lo que este usuario puede abrir (menú + módulos).
  const dockTargets: DockTarget[] = useMemo(() => {
    const out: DockTarget[] = [];
    for (const it of SHELL_NAV) {
      const tg = resolveTarget(it, access);
      if (tg) out.push({ label: it.label, path: toPath(tg), words: NAV_WORDS[it.key] ?? [] });
    }
    for (const m of availableModules) {
      if (m.key === 'orion' || !m.sidebarItems?.length) continue;
      const first = m.sidebarItems.find((i) => !i.comingSoon);
      if (first && canOpen(`${m.key}/${first.tab}`, access)) out.push({ label: m.name, path: toPath(`${m.key}/${first.tab}`), words: [] });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableModules, isAdmin, isDev, sovereign]);
  // nav='modules': la barra lateral muestra los módulos que la org ve hoy (con sus pestañas);
  // nav='orion' (default): la navegación fija del producto Orion + «Más módulos».
  const byModules = config?.nav === 'modules';
  const showHome = config?.home !== false;
  // 2026-10-28 · La tarjeta «Impulsa el crecimiento…» queda oculta para toda org
  // (solo `promo: true` explícito la muestra); su lugar es Configuración, fija.
  const showPromo = config?.promo === true;
  const configItem = SHELL_NAV.find((n) => n.key === 'config')!;
  const configTarget = resolveTarget(configItem, access);
  const nav = byModules
    ? (showHome ? SHELL_NAV.filter((n) => n.key === 'inicio') : [])
        .map((item) => ({ item, target: resolveTarget(item, access) }))
        .filter((n) => n.target)
    : SHELL_NAV.filter((n) => n.key !== 'config')
        .map((item) => ({ item, target: resolveTarget(item, access) })).filter((n) => n.target);
  const covered = new Set(SHELL_NAV.flatMap((n) => n.owns.map((o) => o.split('/')[0])));
  const moreModules = byModules ? [] : availableModules.filter((m) => m.key !== 'orion' && !covered.has(m.key));
  const orgModules = byModules
    ? availableModules
        .filter((m) => m.key !== 'orion' && !(configTarget && m.key === 'ajustes'))
        .map((m) => ({ m, tabs: m.sidebarItems.filter((i) => canOpen(`${m.key}/${i.tab}`, access)) }))
        .filter((x) => x.tabs.length > 0)
    : [];
  const isLegacyRoute = params.moduleKey !== 'orion';

  // Favicon de la marca mientras el shell está activo (se restaura al salir).
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) return;
    const prev = link.href;
    link.href = brand.favicon;
    return () => {
      link.href = prev;
    };
  }, [brand.favicon]);

  // Recordar el módulo (igual que <Layout>).
  useEffect(() => {
    if (params.moduleKey) rememberModule(params.moduleKey);
  }, [params.moduleKey, rememberModule]);

  // Cerrar el cajón al navegar.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  // ⌘K / Ctrl+K abre el buscador.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Cajón móvil: Esc cierra y el foco vuelve al botón de menú.
  useEffect(() => {
    if (!drawerOpen) return;
    const first = drawerRef.current?.querySelector<HTMLElement>('a,button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        menuBtnRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Notificaciones = trabajo real pendiente (no hay feed de notificaciones aún).
  const canSeeChanges = canReviewSite(access);
  const canSeeApprove = canOpen('mkt-meta/approve', access);
  const pendingRevs = useRevisions('pending', 100, canSeeChanges);
  const postCounts = usePostStatusCounts(canSeeApprove);
  const pendingCount = canSeeChanges && Array.isArray(pendingRevs.data) ? pendingRevs.data.length : 0;
  const draftCount = canSeeApprove ? postCounts.data?.draft ?? 0 : 0;
  // 🛎️ Clientes esperando a una persona del equipo (supervisor de Orión).
  const supervisor = useSupervisorAlerts();
  const alertas = supervisor.data?.abiertas ?? [];
  const [atendiendo, setAtendiendo] = useState<string | null>(null);
  const loAtiendo = useCallback(async (a: SupervisorAlert) => {
    setAtendiendo(a.thread_id);
    try {
      await attendSupervisorAlert(a.thread_id);
      await supervisor.refetch();
    } catch {
      /* la alerta sigue abierta: se reintenta desde la campana */
    } finally {
      setAtendiendo(null);
    }
  }, [supervisor]);
  // 🎫 Solicitudes que esperan al CTO (todas las orgs). El backend decide quién
  // es aprobador (allowlist): a un cto fuera de ella le da 403 y no se muestra.
  const ctoPending = useCtoPending(claims.isCto);
  const solicitudes = ctoPending.data?.results ?? [];
  const solicitudesCount = ctoPending.data?.count ?? 0;
  const [decidiendo, setDecidiendo] = useState<string | null>(null);
  const [decision, setDecision] = useState<{ id: string; texto: string; error?: boolean } | null>(null);
  const decidir = useCallback(async (t: CtoPendingItem, aprobar: boolean) => {
    setDecidiendo(t.id);
    setDecision(null);
    try {
      const r = await decideTicket(t.id, aprobar, 'Desde la campana del dashboard');
      setDecision({ id: t.id, texto: `${t.code}: ${r.message}` });
      await ctoPending.refetch();
    } catch (e) {
      setDecision({ id: t.id, texto: `${t.code}: ${e instanceof Error ? e.message : 'no se pudo guardar'}`, error: true });
    } finally {
      setDecidiendo(null);
    }
  }, [ctoPending]);
  const notifTotal = pendingCount + draftCount + alertas.length + solicitudesCount;

  const notif = useDisclosure();
  const orgMenu = useDisclosure();
  const userMenu = useDisclosure();

  const go = useCallback(
    (path: string) => {
      navigate(path);
      notif.setOpen(false);
      userMenu.setOpen(false);
      orgMenu.setOpen(false);
    },
    [navigate, notif, userMenu, orgMenu],
  );

  const styleVars = useMemo(() => themeVars(theme, mode) as React.CSSProperties, [theme, mode]);
  const advisoryUrl = 'https://orion.brainstream.pro/diagnostico#agenda';

  const sidebar = (
    <div className="flex h-full flex-col gap-4 px-3 py-4">
      <div className="flex items-center px-1">
        {/* Marca v2 (fondo oscuro de la barra en ambos temas). */}
        <img
          src={brand.logoDark}
          alt={brand.name}
          width={194}
          height={60}
          className="h-[52px] w-auto select-none"
          draggable={false}
        />
      </div>

      <div role="tablist" aria-label="Vista de la barra lateral" className="grid grid-cols-2 gap-1 rounded-xl bg-[color:var(--sidebar-hover)] p-1">
        {([['trabajo', 'Trabajo', Briefcase], ['chats', 'Chats', MessagesSquare]] as const).map(([k, label, Icon]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={sideMode === k}
            onClick={() => setSideMode(k)}
            className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium transition-colors ${sideMode === k ? 'bg-[color:var(--sidebar-bg)] text-[color:var(--sidebar-fg)] shadow-sm' : 'text-[color:var(--sidebar-muted)] hover:text-[color:var(--sidebar-fg)]'}`}
          >
            <Icon size={14} aria-hidden /> {label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => go(`/m/orion/asistente?nuevo=${Date.now()}`)}
        className="orion-btn-brand flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold"
      >
        <Plus size={16} aria-hidden /> Nuevo chat
      </button>

      {sideMode === 'chats' ? (
        <SidebarChats
          activeId={new URLSearchParams(location.search).get('hilo')}
          refreshKey={location.pathname + location.search}
          onOpen={(id) => go(`/m/orion/asistente?hilo=${id}`)}
        />
      ) : (
      <nav aria-label="Principal" className="-mx-1 flex-1 overflow-y-auto px-1">
        <ul className="flex flex-col gap-0.5">
          {nav.map(({ item, target }) => {
            const active = ownsRoute(item, params.moduleKey, params.tab);
            return (
              <li key={item.key}>
                <a
                  href={toPath(target!)}
                  onClick={(e) => {
                    e.preventDefault();
                    go(toPath(target!));
                  }}
                  aria-current={active ? 'page' : undefined}
                  className={`relative flex items-center gap-3 rounded-lg py-2 text-sm transition-colors ${
                    item.child ? 'pl-10 pr-3 text-[13px]' : 'px-3'
                  } ${
                    active
                      ? 'bg-[color:var(--sidebar-active-accent)] font-semibold text-[color:var(--sidebar-fg)]'
                      : 'text-[color:var(--sidebar-muted)] hover:bg-[color:var(--sidebar-hover)] hover:text-[color:var(--sidebar-fg)]'
                  }`}
                >
                  {active && (
                    <span aria-hidden className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r bg-[color:var(--sidebar-accent)]" />
                  )}
                  {!item.child && <span aria-hidden className="shrink-0">{item.icon}</span>}
                  <span className="truncate">{item.label}</span>
                </a>
              </li>
            );
          })}
        </ul>

        {orgModules.length > 0 && (
          <ul aria-label="Módulos" className="mt-0.5 flex flex-col gap-0.5">
            {orgModules.map(({ m, tabs }) => {
              const r = resolveModule(m, archetype);
              const active = params.moduleKey === m.key;
              const first = tabs[0];
              return (
                <li key={m.key}>
                  <a
                    href={`/m/${m.key}/${first.tab}`}
                    onClick={(e) => {
                      e.preventDefault();
                      go(`/m/${m.key}/${first.tab}`);
                    }}
                    aria-current={active && tabs.length === 1 ? 'page' : undefined}
                    className={`relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                      active
                        ? 'bg-[color:var(--sidebar-active-accent)] font-semibold text-[color:var(--sidebar-fg)]'
                        : 'text-[color:var(--sidebar-muted)] hover:bg-[color:var(--sidebar-hover)] hover:text-[color:var(--sidebar-fg)]'
                    }`}
                  >
                    {active && (
                      <span aria-hidden className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r bg-[color:var(--sidebar-accent)]" />
                    )}
                    <span aria-hidden className="shrink-0 [&>svg]:h-[18px] [&>svg]:w-[18px]">{m.icon}</span>
                    <span className="truncate">{r.name}</span>
                  </a>
                  {active && tabs.length > 1 && (
                    <ul className="mb-1 mt-0.5 flex flex-col gap-0.5">
                      {tabs.map((t) => {
                        const on = params.tab === t.tab;
                        return (
                          <li key={t.tab}>
                            <a
                              href={`/m/${m.key}/${t.tab}`}
                              onClick={(e) => {
                                e.preventDefault();
                                go(`/m/${m.key}/${t.tab}`);
                              }}
                              aria-current={on ? 'page' : undefined}
                              className={`block truncate rounded-lg py-1.5 pl-10 pr-3 text-[13px] ${
                                on
                                  ? 'font-semibold text-[color:var(--sidebar-fg)]'
                                  : 'text-[color:var(--sidebar-muted)] hover:bg-[color:var(--sidebar-hover)] hover:text-[color:var(--sidebar-fg)]'
                              }`}
                            >
                              {resolveItemLabel(m, t, archetype)}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {moreModules.length > 0 && (
          <div className="mt-3 border-t border-[color:var(--sidebar-border)] pt-3">
            <button
              type="button"
              aria-expanded={moreOpen}
              aria-controls="orion-more-modules"
              onClick={() => setMoreOpen((v) => !v)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-[color:var(--sidebar-muted)] hover:bg-[color:var(--sidebar-hover)] hover:text-[color:var(--sidebar-fg)]"
            >
              <LayoutGrid size={18} aria-hidden />
              <span className="flex-1 text-left">Más módulos</span>
              <ChevronDown size={15} aria-hidden className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
            </button>
            {moreOpen && (
              <ul id="orion-more-modules" className="mt-1 flex flex-col gap-0.5">
                {moreModules.map((m) => {
                  const r = resolveModule(m, archetype);
                  const first = m.sidebarItems.find((i) => canOpen(`${m.key}/${i.tab}`, access));
                  if (!first) return null;
                  const active = params.moduleKey === m.key;
                  return (
                    <li key={m.key}>
                      <a
                        href={`/m/${m.key}/${first.tab}`}
                        onClick={(e) => {
                          e.preventDefault();
                          go(`/m/${m.key}/${first.tab}`);
                        }}
                        aria-current={active ? 'page' : undefined}
                        className={`block truncate rounded-lg py-1.5 pl-10 pr-3 text-[13px] ${
                          active
                            ? 'bg-[color:var(--sidebar-active-accent)] font-semibold text-[color:var(--sidebar-fg)]'
                            : 'text-[color:var(--sidebar-muted)] hover:bg-[color:var(--sidebar-hover)] hover:text-[color:var(--sidebar-fg)]'
                        }`}
                      >
                        {r.name}
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </nav>
      )}

      {configTarget && sideMode === 'trabajo' && (() => {
        const active = ownsRoute(configItem, params.moduleKey, params.tab);
        return (
          <a
            href={toPath(configTarget)}
            onClick={(e) => {
              e.preventDefault();
              go(toPath(configTarget));
            }}
            aria-current={active ? 'page' : undefined}
            className={`relative -mt-2 flex items-center gap-3 rounded-lg border-t border-[color:var(--sidebar-border)] px-3 py-2.5 text-sm transition-colors ${
              active
                ? 'bg-[color:var(--sidebar-active-accent)] font-semibold text-[color:var(--sidebar-fg)]'
                : 'text-[color:var(--sidebar-muted)] hover:bg-[color:var(--sidebar-hover)] hover:text-[color:var(--sidebar-fg)]'
            }`}
          >
            {active && (
              <span aria-hidden className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r bg-[color:var(--sidebar-accent)]" />
            )}
            <span aria-hidden className="shrink-0">{configItem.icon}</span>
            <span className="truncate">{configItem.label}</span>
          </a>
        );
      })()}

      {showPromo && sideMode === 'trabajo' && (
      <div className="rounded-2xl border border-[color:var(--sidebar-border)] bg-[color:var(--sidebar-hover)] p-4">
        <p className="text-sm font-semibold text-[color:var(--sidebar-fg)]">Impulsa el crecimiento de tu organización</p>
        <p className="mt-1 text-xs leading-relaxed text-[color:var(--sidebar-muted)]">
          Agenda una asesoría con el equipo de Orion para decidir qué automatizar primero.
        </p>
        <a
          href={advisoryUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[color:var(--sidebar-border)] px-3 py-2 text-sm font-medium text-[color:var(--sidebar-fg)] hover:bg-[color:var(--sidebar-active)]"
        >
          Solicitar asesoría <ExternalLink size={14} aria-hidden />
          <span className="sr-only">(se abre en otra pestaña)</span>
        </a>
      </div>
      )}
    </div>
  );

  return (
    <div className="orion-shell flex min-h-[100svh]" style={styleVars}>
      <a
        href="#orion-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[200] focus:rounded-lg focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:text-foreground focus:shadow-lg"
      >
        Saltar al contenido
      </a>

      {/* Barra lateral fija (escritorio) */}
      <div className={`orion-sidebar-col hidden w-64 shrink-0 bg-[color:var(--sidebar-bg)] ${collapsed ? 'lg:hidden' : 'lg:block'}`}>
        <aside className="orion-sidebar sticky top-0 h-[100svh]">{sidebar}</aside>
      </div>

      {/* Cajón (móvil / tableta) */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[120] lg:hidden">
          <div className="absolute inset-0 bg-black/50" aria-hidden onClick={() => setDrawerOpen(false)} />
          <aside
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menú"
            className="orion-sidebar app-drawer-safe absolute inset-y-0 left-0 w-[86vw] max-w-[300px] bg-[color:var(--sidebar-bg)] shadow-2xl"
          >
            <button
              type="button"
              onClick={() => {
                setDrawerOpen(false);
                menuBtnRef.current?.focus();
              }}
              aria-label="Cerrar menú"
              className="app-drawer-close absolute right-2 top-3 rounded-lg p-2 text-[color:var(--sidebar-muted)] hover:bg-[color:var(--sidebar-hover)] hover:text-[color:var(--sidebar-fg)]"
            >
              <X size={18} />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="app-header-safe sticky top-0 z-40 flex h-16 items-center gap-2 border-b border-border bg-background/90 px-3 backdrop-blur sm:gap-3 sm:px-6">
          <button
            ref={menuBtnRef}
            type="button"
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Mostrar menú (Ctrl+B)' : 'Ocultar menú (Ctrl+B)'}
            title={collapsed ? 'Mostrar menú (Ctrl+B)' : 'Ocultar menú (Ctrl+B)'}
            aria-expanded={drawerOpen || !collapsed}
            className="rounded-lg p-2 text-foreground hover:bg-muted"
          >
            <span className="lg:hidden"><Menu size={20} /></span>
            <span className="hidden lg:inline">{collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}</span>
          </button>

          {/* Selector de organización: solo la propia (multi-org real = god-mode, aparte) */}
          <div ref={orgMenu.ref} className="relative min-w-0 max-w-[40vw] shrink sm:max-w-xs">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={orgMenu.open}
              onClick={() => orgMenu.setOpen(!orgMenu.open)}
              className="flex w-full min-w-0 items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-1.5 text-sm text-foreground hover:bg-muted"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Building2 size={14} aria-hidden />
              </span>
              <span className="truncate font-medium">{orgLabel}</span>
              <ChevronDown size={14} aria-hidden className="shrink-0 text-muted-foreground" />
            </button>
            {orgMenu.open && (
              <div role="menu" aria-label="Organización" className="absolute left-0 top-full z-50 mt-2 w-72 rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-xl">
                <p className="px-2.5 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">Tu organización</p>
                <div role="menuitemradio" aria-checked="true" className="flex items-center gap-2 rounded-lg bg-muted px-2.5 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate font-medium">{orgLabel}</span>
                  <Check size={15} aria-hidden className="text-primary" />
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="ml-auto flex shrink-0 items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-2 text-sm text-muted-foreground hover:bg-muted sm:ml-2 sm:min-w-[240px] md:min-w-[320px]"
            aria-label="Buscar (Ctrl+K)"
          >
            <Search size={16} aria-hidden />
            <span className="hidden flex-1 text-left sm:inline">Buscar módulos y acciones…</span>
            <kbd className="hidden rounded-md border border-border px-1.5 py-0.5 font-sans text-[11px] text-muted-foreground md:inline">⌘K</kbd>
          </button>

          <div className="flex shrink-0 items-center gap-1 sm:ml-auto sm:gap-2">
            {config?.model_label && (
              <span
                className="hidden items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground xl:inline-flex"
                title="Modelo configurado para tu organización (solo lectura)"
              >
                <Cpu size={13} aria-hidden className="text-primary" />
                <span className="sr-only">Modelo de IA:</span>
                <span className="font-medium text-foreground">{config.model_label}</span>
              </span>
            )}

            <button
              type="button"
              onClick={toggleMode}
              aria-label={mode === 'dark' ? 'Usar tema claro' : 'Usar tema oscuro'}
              className="rounded-lg p-2 text-foreground hover:bg-muted"
            >
              {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <div ref={notif.ref} className="relative">
              <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded={notif.open}
                onClick={() => notif.setOpen(!notif.open)}
                aria-label={notifTotal > 0 ? `Pendientes: ${notifTotal}` : 'Pendientes'}
                className="relative rounded-lg p-2 text-foreground hover:bg-muted"
              >
                <Bell size={18} />
                {notifTotal > 0 && (
                  <span aria-hidden className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                    {notifTotal > 99 ? '99+' : notifTotal}
                  </span>
                )}
              </button>
              {notif.open && (
                <div role="dialog" aria-label="Pendientes" className="absolute right-0 top-full z-50 mt-2 w-[min(320px,calc(100vw-24px))] rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-xl">
                  <p className="px-2 pb-2 pt-1 text-sm font-semibold">Pendientes</p>
                  {decision && (
                    <p role="status" className={`mx-2 mb-2 rounded-md px-2 py-1 text-xs ${decision.error ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                      {decision.texto}
                    </p>
                  )}
                  {notifTotal === 0 ? (
                    <p className="px-2 pb-2 text-sm text-muted-foreground">No tienes nada pendiente por revisar.</p>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {alertas.slice(0, 5).map((a) => (
                        <li key={a.thread_id} className={`rounded-lg border px-2 py-2 ${a.vencida ? 'border-destructive/40 bg-destructive/5' : 'border-border'}`}>
                          <button
                            type="button"
                            onClick={() => go(`/m/conversaciones/todas?hilo=${encodeURIComponent(a.thread_id)}&tel=${encodeURIComponent(a.telefono || '')}`)}
                            className="w-full text-left text-sm"
                          >
                            <span className="font-medium">{alertaTitulo(a)}</span>
                            <span className="block text-xs text-muted-foreground">{alertaDetalle(a)}</span>
                          </button>
                          <div className="mt-1.5 flex justify-end">
                            <button
                              type="button"
                              disabled={atendiendo === a.thread_id}
                              onClick={() => loAtiendo(a)}
                              className="rounded-md border border-border px-2 py-0.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
                            >
                              {atendiendo === a.thread_id ? 'Guardando…' : 'Lo atiendo'}
                            </button>
                          </div>
                        </li>
                      ))}
                      {solicitudes.slice(0, 5).map((t) => (
                        <li key={t.id} className="rounded-lg border border-border px-2 py-2">
                          <button
                            type="button"
                            onClick={() => go(`/m/orion/tickets?ticket=${encodeURIComponent(t.id)}`)}
                            className="w-full text-left text-sm"
                          >
                            <span className="font-medium">{t.title}</span>
                            <span className="block text-xs text-muted-foreground">
                              {t.code} · {t.org_id}{t.solicitante ? ` · ${t.solicitante}` : ''} · {fechaSolicitud(t.detected_at)}
                            </span>
                          </button>
                          <div className="mt-1.5 flex justify-end gap-1.5">
                            <button
                              type="button"
                              disabled={decidiendo === t.id}
                              onClick={() => decidir(t, false)}
                              className="rounded-md border border-border px-2 py-0.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
                            >
                              Rechazar
                            </button>
                            <button
                              type="button"
                              disabled={decidiendo === t.id}
                              onClick={() => decidir(t, true)}
                              className="rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                            >
                              {decidiendo === t.id ? 'Guardando…' : 'Aprobar'}
                            </button>
                          </div>
                        </li>
                      ))}
                      {solicitudesCount > 5 && (
                        <li>
                          <button type="button" onClick={() => go('/m/orion/tickets')} className="w-full rounded-lg px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted">
                            y {solicitudesCount - 5} solicitudes más esperando
                          </button>
                        </li>
                      )}
                      {alertas.length > 5 && (
                        <li className="px-2 text-xs text-muted-foreground">y {alertas.length - 5} más esperando</li>
                      )}
                      {pendingCount > 0 && (
                        <li>
                          <button type="button" onClick={() => go('/m/orion/cambios-sitio')} className="w-full rounded-lg px-2 py-2 text-left text-sm hover:bg-muted">
                            <span className="font-medium">{pendingCount} {pendingCount === 1 ? 'cambio' : 'cambios'} del sitio</span>
                            <span className="block text-xs text-muted-foreground">Esperan tu aprobación</span>
                          </button>
                        </li>
                      )}
                      {draftCount > 0 && (
                        <li>
                          <button type="button" onClick={() => go('/m/mkt-meta/approve')} className="w-full rounded-lg px-2 py-2 text-left text-sm hover:bg-muted">
                            <span className="font-medium">{draftCount} {draftCount === 1 ? 'borrador' : 'borradores'} de contenido</span>
                            <span className="block text-xs text-muted-foreground">Listos para revisar</span>
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div ref={userMenu.ref} className="relative">
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={userMenu.open}
                onClick={() => userMenu.setOpen(!userMenu.open)}
                className="flex items-center gap-2 rounded-xl p-1 pr-1.5 hover:bg-muted"
                aria-label={`Cuenta de ${displayName}`}
              >
                <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                  {initials(displayName)}
                </span>
                <span className="hidden max-w-[140px] truncate text-sm font-medium text-foreground xl:inline">{displayName}</span>
                <ChevronDown size={14} aria-hidden className="hidden text-muted-foreground xl:inline" />
              </button>
              {userMenu.open && (
                <div role="menu" aria-label="Cuenta" className="absolute right-0 top-full z-50 mt-2 w-64 rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-xl">
                  <div className="px-2.5 py-2">
                    <p className="truncate text-sm font-semibold">{myName.data || 'Tu cuenta'}</p>
                    <p className="truncate text-xs text-muted-foreground">{claims.email}</p>
                  </div>
                  {canOpen('ajustes/inicio', access) && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => go('/m/ajustes/inicio?s=perfil')}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-muted"
                    >
                      <UserRound size={15} aria-hidden /> Mi perfil y cómo me habla la IA
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={onLogout}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-muted"
                  >
                    <LogOut size={15} aria-hidden /> Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main id="orion-main" tabIndex={-1} className="min-w-0 flex-1 outline-none">
          {isLegacyRoute ? (
            // Páginas existentes: se conservan tal cual dentro del marco nuevo.
            // `.o-page` mantiene sus adaptadores claro/oscuro de index.css.
            <div className="p-3 sm:p-6">
              <div className="o-page orion-legacy-page">{children}</div>
            </div>
          ) : (
            children
          )}
        </main>
        <AgentDock targets={dockTargets} hidden={params.moduleKey === 'orion' && params.tab === 'asistente'} />
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} access={access} archetype={archetype} />
    </div>
  );
}

function initials(s: string): string {
  const base = (s || '').split('@')[0].replace(/[._-]+/g, ' ').trim();
  const parts = base.split(/\s+/).filter(Boolean);
  const out = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  return (out || '·').toUpperCase();
}

export default OrionShell;

// ── 🛎️ Alertas del supervisor ────────────────────────────────────────────
const MOTIVO: Record<string, string> = {
  lead_registrado: 'prospecto registrado',
  promesa_equipo: 'el agente prometió que lo contactan',
  pidio_asesor: 'pidió hablar con una persona',
  grupo_grande: 'grupo grande',
};

function esperaDe(desde: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(desde).getTime()) / 60000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return h < 48 ? `${h} h` : `${Math.floor(h / 24)} días`;
}

function alertaTitulo(a: SupervisorAlert): string {
  const quien = a.cliente || a.telefono || 'Cliente';
  const grupo = a.grupo ? ` · grupo de ${a.grupo}` : '';
  return `${a.vencida ? 'Sin atender' : 'Esperando'}: ${quien}${grupo}`;
}

/** «hoy», «ayer», «hace 5 días» o, si pasan de 30 días, la fecha («20 jul»): sin ella no se
 *  distingue una solicitud de ayer de una de hace meses que quizá ya no sirve. */
function fechaSolicitud(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dias = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias <= 30) return `hace ${dias} días`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

function alertaDetalle(a: SupervisorAlert): string {
  const motivos = (a.motivos || []).map((m) => MOTIVO[m] || m).join(' · ');
  const fecha = a.fecha_servicio ? ` · para el ${a.fecha_servicio}` : '';
  return `Espera ${esperaDe(a.desde)}${fecha}${motivos ? ` · ${motivos}` : ''}`;
}
